import React, { useMemo, useState } from "react";
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { createAddendum, updateAddendum } from "./api";
import { toYmd } from "../../../../../../utils/dateOnly";

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);

const sectionTitle = {
  fontSize: 13,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#0c447c",
  borderBottom: "2px solid #0c447c",
  paddingBottom: 4,
  margin: "18px 0 12px",
};
const amendRow = { border: "1px solid #e6e9ee", borderRadius: 8, padding: "12px 14px", marginBottom: 10 };

/**
 * Create / edit a Lease Addendum from the NewMasterSheet tenant modal. Prefilled
 * from the MasterSheet item (`item`), with tenant signature slots seeded from the
 * unit's tenants. Dynamic: amendment checkboxes reveal their fields; the new
 * total recomputes live.
 */
const AddendumModal = ({
  isOpen,
  toggle,
  itemId,
  item = {},
  tenants = [],
  buildingName,
  propertyName = "",
  unitNo,
  defaults = {},
  existing,
  onSaved,
}) => {
  const propertyAddress = `${propertyName ? propertyName + " " : ""}${buildingName || ""}`.trim();
  const prefill = useMemo(() => {
    if (existing) return existing;
    const parkingTotal = num(item.ssFee) + num(item.ugFee);
    const petTotal = num(item.dogFee) + num(item.catFee);
    const defaultParkingType =
      num(item.ugFee) > 0 && num(item.ssFee) > 0 ? "both"
      : num(item.ugFee) > 0 ? "underground"
      : "surface";
    const names =
      typeof item.tenants === "string" && item.tenants
        ? item.tenants
        : (tenants || [])
            .map((t) => t.name || `${t.first_name || t.firstName || ""} ${t.last_name || t.lastName || ""}`.trim())
            .filter(Boolean)
            .join(", ");
    return {
      tenant_names: names,
      original_lease_date: toYmd(item.leaseStartDmy),
      effective_date: "",
      amendment_details: {
        parking: { selected: false, action: "added", parking_type: defaultParkingType, new_monthly_charge: parkingTotal },
        pet: { selected: false, action: "added", description: "Dog", non_refundable_fee: 0, new_monthly_fee: petTotal },
        others: [],
      },
      revised_rent: {
        new_base_rent: num(item.basicRent),
        new_parking_charge: parkingTotal,
        new_pet_fee: petTotal,
        new_storage_fee: num(item.storageFee),
        new_other_charges: num(item.otherFee),
      },
    };
  }, [existing, item, tenants, defaults, buildingName, unitNo]);

  const [form, setForm] = useState(prefill);
  const [saving, setSaving] = useState(false);

  const set = (path, value) => {
    setForm((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const rr = form.revised_rent || {};
  const newTotal =
    num(rr.new_base_rent) + num(rr.new_parking_charge) + num(rr.new_pet_fee) + num(rr.new_storage_fee) + num(rr.new_other_charges);

  const addOtherLine = () =>
    set("amendment_details.others", [...(form.amendment_details.others || []), { label: "", amount: 0 }]);
  const removeOtherLine = (i) =>
    set("amendment_details.others", (form.amendment_details.others || []).filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.effective_date) {
      toast.error("Effective date is required");
      return;
    }
    setSaving(true);
    try {
      const tenantSignatures = (tenants || []).length
        ? tenants.map((t, idx) => ({
            tenant_index: idx,
            name: t.name || `${t.first_name || t.firstName || ""} ${t.last_name || t.lastName || ""}`.trim(),
            email: t.email || "",
            signed: false,
          }))
        : undefined;

      const payload = {
        mastersheet_item_id: itemId,
        property_id: defaults.property_id,
        building_name: buildingName,
        unit_no: unitNo,
        property_address: propertyAddress,
        tenant_names: form.tenant_names,
        original_lease_date: form.original_lease_date || undefined,
        effective_date: form.effective_date,
        amendment_details: form.amendment_details,
        revised_rent: { ...form.revised_rent, new_total_monthly_rent: newTotal },
        ...(tenantSignatures ? { tenant_signatures: tenantSignatures } : {}),
      };
      if (existing?._id) {
        await updateAddendum(existing._id, payload);
        toast.success("Addendum updated");
      } else {
        await createAddendum(payload);
        toast.success("Addendum created");
      }
      onSaved && onSaved();
      toggle();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save addendum");
    } finally {
      setSaving(false);
    }
  };

  const ad = form.amendment_details || {};

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>{existing ? "Edit lease addendum" : "Create lease addendum"}</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <small className="text-muted d-block">Property</small>
          <div style={{ fontWeight: 600 }}>{propertyAddress || "—"}</div>
        </div>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label>Tenant(s)</Label>
              <Input value={form.tenant_names || ""} onChange={(e) => set("tenant_names", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Original lease date</Label>
              <Input type="date" value={toYmd(form.original_lease_date)} onChange={(e) => set("original_lease_date", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Effective date of amendment</Label>
              <Input type="date" value={toYmd(form.effective_date)} onChange={(e) => set("effective_date", e.target.value)} />
            </FormGroup>
          </Col>
        </Row>

        <div style={sectionTitle}>Amendment details</div>

        <div style={amendRow}>
          <Label check style={{ fontWeight: 600 }}>
            <Input type="checkbox" checked={!!ad.parking?.selected} onChange={(e) => set("amendment_details.parking.selected", e.target.checked)} /> Parking added / removed
          </Label>
          {ad.parking?.selected && (
            <Row className="mt-2">
              <Col md={3}>
                <Label>Action</Label>
                <Input type="select" value={ad.parking.action} onChange={(e) => set("amendment_details.parking.action", e.target.value)}>
                  <option value="added">Added</option>
                  <option value="removed">Removed</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label>Parking type</Label>
                <Input type="select" value={ad.parking.parking_type || "surface"} onChange={(e) => set("amendment_details.parking.parking_type", e.target.value)}>
                  <option value="surface">Surface (SS Fee)</option>
                  <option value="underground">Underground (UG Fee)</option>
                  <option value="both">Both</option>
                </Input>
              </Col>
              <Col md={4}>
                <Label>New monthly parking charge</Label>
                <Input type="number" value={ad.parking.new_monthly_charge} onChange={(e) => set("amendment_details.parking.new_monthly_charge", e.target.value)} />
              </Col>
            </Row>
          )}
        </div>

        <div style={amendRow}>
          <Label check style={{ fontWeight: 600 }}>
            <Input type="checkbox" checked={!!ad.pet?.selected} onChange={(e) => set("amendment_details.pet.selected", e.target.checked)} /> Pet fee added / removed
          </Label>
          {ad.pet?.selected && (
            <Row className="mt-2">
              <Col md={3}>
                <Label>Pet</Label>
                <Input type="select" value={ad.pet.description} onChange={(e) => set("amendment_details.pet.description", e.target.value)}>
                  <option>Dog</option>
                  <option>Cat</option>
                  <option>Other</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label>Action</Label>
                <Input type="select" value={ad.pet.action} onChange={(e) => set("amendment_details.pet.action", e.target.value)}>
                  <option value="added">Added</option>
                  <option value="removed">Removed</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label>Non-refundable fee</Label>
                <Input type="number" value={ad.pet.non_refundable_fee} onChange={(e) => set("amendment_details.pet.non_refundable_fee", e.target.value)} />
              </Col>
              <Col md={3}>
                <Label>New monthly pet fee</Label>
                <Input type="number" value={ad.pet.new_monthly_fee} onChange={(e) => set("amendment_details.pet.new_monthly_fee", e.target.value)} />
              </Col>
            </Row>
          )}
        </div>

        <div style={amendRow}>
          <Label check style={{ fontWeight: 600 }}>
            <Input
              type="checkbox"
              checked={(ad.others || []).length > 0}
              onChange={(e) => set("amendment_details.others", e.target.checked ? [{ label: "", amount: 0 }] : [])}
            /> Other amendment (Storage / Telus / custom)
          </Label>
          {(ad.others || []).length > 0 && (
            <div className="mt-2">
              {ad.others.map((line, i) => (
                <Row key={i} className="mb-2 align-items-end">
                  <Col md={6}>
                    <Label>Label</Label>
                    <Input value={line.label} placeholder="e.g. Storage locker, Telus internet" onChange={(e) => set(`amendment_details.others.${i}.label`, e.target.value)} />
                  </Col>
                  <Col md={4}>
                    <Label>Amount</Label>
                    <Input type="number" value={line.amount} onChange={(e) => set(`amendment_details.others.${i}.amount`, e.target.value)} />
                  </Col>
                  <Col md={2}>
                    <Button color="link" className="text-danger p-0" onClick={() => removeOtherLine(i)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button color="link" className="p-0" onClick={addOtherLine}>
                + Add line item
              </Button>
            </div>
          )}
        </div>

        <div style={sectionTitle}>Revised monthly rent</div>
        <Row>
          {[
            ["new_base_rent", "New base rent"],
            ["new_parking_charge", "New parking charge"],
            ["new_pet_fee", "New pet fee"],
            ["new_storage_fee", "New storage fee"],
            ["new_other_charges", "New other charges"],
          ].map(([key, label]) => (
            <Col md={4} key={key}>
              <FormGroup>
                <Label>{label}</Label>
                <Input type="number" value={rr[key]} onChange={(e) => set(`revised_rent.${key}`, e.target.value)} />
              </FormGroup>
            </Col>
          ))}
        </Row>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f1f6fc", borderRadius: 8, padding: "12px 16px", marginTop: 6 }}>
          <span style={{ fontWeight: 600, color: "#0c447c" }}>New total monthly rent payable</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: "#0c447c" }}>
            ${newTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        <div className="text-muted mt-3" style={{ fontSize: 12 }}>
          All listed tenants must e-sign, then a manager marks the addendum completed to apply the changes to the mastersheet.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : existing ? "Save changes" : "Save draft"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddendumModal;
